import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Lightbulb, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const ONBOARDING_KEY = 'ibooksport_organizer_onboarding_done';

// ==================== HUB TOUR STEPS ====================
const tourSteps = [
  { target: 'hub-btn-events', title: 'Vos Evenements', description: 'Creez et gerez tous vos evenements sportifs. Ajoutez des courses, tarifs, dates et personnalisez chaque page.' },
  { target: 'hub-btn-participants', title: 'Participants', description: 'Consultez la liste de tous vos inscrits, filtrez par evenement, exportez les donnees en CSV.' },
  { target: 'hub-btn-gauges', title: 'Jauges de remplissage', description: 'Visualisez en temps reel le taux de remplissage de chacun de vos evenements.' },
  { target: 'hub-btn-checkin', title: 'Check-in', description: 'Scannez les QR codes ou recherchez un participant pour valider sa presence le jour J.' },
  { target: 'hub-btn-finances', title: 'Finances', description: 'Suivez vos revenus en detail : inscriptions, ventes boutique, marges et commissions.' },
  { target: 'hub-btn-escrow', title: 'Compte Sequestre', description: 'Consultez les fonds en attente de virement. Apres cloture des inscriptions, demandez le virement vers votre IBAN.' },
  { target: 'hub-btn-correspondances', title: 'Correspondances', description: 'Envoyez des messages et emails a vos participants : informations pratiques, rappels, etc.' },
  { target: 'hub-btn-share', title: 'Partage', description: 'Partagez vos evenements sur les reseaux sociaux et generez des liens de partage personnalises.' },
  { target: 'hub-btn-contact-admin', title: 'Contact Admin', description: 'Contactez le support iBookSport pour toute question relative a vos finances ou a la plateforme.' },
  { target: 'hub-btn-chronometrage', title: 'Chronometrage', description: 'Importez les temps de course, exportez les listes de dossards et gerez le chronometrage de vos epreuves.' },
  { target: 'hub-btn-results', title: 'Resultats', description: 'Publiez les classements et performances de vos participants apres chaque evenement.' },
  { target: 'hub-btn-volunteers', title: 'Benevoles', description: 'Gerez vos equipes de benevoles : coordonnees, roles assignes, repartition par evenement.' },
  { target: 'hub-btn-partners', title: 'Prestations Externes', description: 'Gerez vos prestataires (traiteurs, securite, animation...) et centralisez leurs coordonnees.' },
  { target: 'hub-btn-bookings', title: 'Reservation Entreprises', description: 'Gerez les reservations d\'equipes et les tarifs de groupe pour les entreprises.' },
  { target: 'hub-btn-sponsors', title: 'Sponsors & Donateurs', description: 'Suivez vos sponsors, leurs contributions et les contreparties associees.' },
  { target: 'hub-btn-boutique', title: 'Boutique Produits', description: 'Gerez votre catalogue de produits derives. Commandez via un prestataire, fixez vos prix et vendez directement.' },
  { target: 'hub-btn-analytics', title: 'Statistiques', description: 'Analysez vos performances avec des graphiques avances : tendances, conversions, revenus par periode.' },
  { target: 'hub-btn-rfid', title: 'Location RFID', description: 'Louez du materiel de chronometrage RFID pour vos evenements directement depuis la plateforme.' },
  { target: 'hub-btn-sms', title: 'Notifications SMS', description: 'Envoyez des SMS a vos participants : rappels, informations de derniere minute, resultats.' },
  { target: 'create-event-btn', title: 'Creer un evenement', description: 'Cliquez ici pour creer votre premier evenement. Le formulaire vous guidera etape par etape !' },
];

// ==================== MODULE HELP CONTENT ====================
const moduleHelp = {
  events: {
    title: 'Aide — Evenements',
    tips: [
      { title: 'Creer un evenement', text: 'Cliquez sur "+ Nouvel evenement" en haut a droite. Remplissez titre, sport, lieu, date et tarif.' },
      { title: 'Ajouter des courses', text: 'Dans la creation, ajoutez plusieurs epreuves (10km, semi, marathon) avec distances et tarifs differents.' },
      { title: 'Publier / Depublier', text: 'Un evenement publie apparait sur la page publique. Vous pouvez le depublier a tout moment.' },
      { title: 'Modifier', text: 'Cliquez sur l\'icone crayon pour modifier les details, dates ou tarifs d\'un evenement existant.' },
    ],
  },
  participants: {
    title: 'Aide — Participants',
    tips: [
      { title: 'Filtrer par evenement', text: 'Utilisez le menu deroulant pour afficher les inscrits d\'un evenement specifique.' },
      { title: 'Rechercher', text: 'Tapez un nom, email ou numero de dossard dans la barre de recherche.' },
      { title: 'Exporter en CSV', text: 'Le bouton d\'export genere un fichier CSV avec tous les participants affiches.' },
    ],
  },
  gauges: {
    title: 'Aide — Jauges',
    tips: [
      { title: 'Taux de remplissage', text: 'Chaque jauge montre le ratio inscrits/places max pour chaque evenement.' },
      { title: 'Couleurs', text: 'Vert = moins de 70%, Orange = 70-90%, Rouge = plus de 90% de remplissage.' },
    ],
  },
  checkin: {
    title: 'Aide — Check-in',
    tips: [
      { title: 'Scanner un QR code', text: 'Utilisez la camera de votre telephone pour scanner le QR code du participant.' },
      { title: 'Recherche manuelle', text: 'Recherchez par nom ou numero de dossard si le QR code ne fonctionne pas.' },
      { title: 'Marquer comme recupere', text: 'Cliquez sur le bouton pour confirmer la recuperation du dossard.' },
    ],
  },
  finances: {
    title: 'Aide — Finances',
    tips: [
      { title: 'Vue d\'ensemble', text: 'Retrouvez le total des inscriptions, les frais de plateforme (5%) et votre revenu net.' },
      { title: 'Detail par evenement', text: 'Chaque evenement affiche ses propres revenus, nombre d\'inscrits et frais.' },
      { title: 'Exporter', text: 'Exportez vos finances en CSV ou PDF pour votre comptabilite.' },
    ],
  },
  escrow: {
    title: 'Aide — Compte Sequestre',
    tips: [
      { title: 'Fonctionnement', text: 'Les paiements des participants sont conserves en sequestre jusqu\'a la cloture des inscriptions.' },
      { title: 'Demander un virement', text: 'Apres cloture, cliquez sur "Demander le virement" et renseignez votre IBAN.' },
      { title: 'Commission 5%', text: 'Les frais de plateforme (5%) sont preleves automatiquement avant le virement.' },
    ],
  },
  correspondances: {
    title: 'Aide — Correspondances',
    tips: [
      { title: 'Envoyer un message', text: 'Cliquez sur "Nouveau message" pour rediger une communication a vos inscrits.' },
      { title: 'Cibler par evenement', text: 'Choisissez un evenement specifique ou envoyez a tous vos participants.' },
      { title: 'Email optionnel', text: 'Activez l\'option "Envoyer par email" pour que le message arrive aussi dans leur boite mail.' },
    ],
  },
  share: {
    title: 'Aide — Partage',
    tips: [
      { title: 'Lien de partage', text: 'Copiez le lien direct vers votre page evenement pour le partager.' },
      { title: 'Reseaux sociaux', text: 'Partagez en un clic sur Facebook, Twitter, Instagram ou par email.' },
    ],
  },
  'contact-admin': {
    title: 'Aide — Contact Admin',
    tips: [
      { title: 'Support', text: 'Envoyez un message a l\'equipe iBookSport pour toute question technique ou financiere.' },
      { title: 'Delai de reponse', text: 'L\'equipe repond generalement sous 24h en jours ouvres.' },
    ],
  },
  chronometrage: {
    title: 'Aide — Chronometrage',
    tips: [
      { title: 'Importer des temps', text: 'Selectionnez un evenement puis importez un fichier CSV avec les temps de passage.' },
      { title: 'Format attendu', text: 'Le CSV doit contenir : numero de dossard, temps (HH:MM:SS).' },
      { title: 'Export dossards', text: 'Exportez la liste des dossards pour la preparer avant le jour J.' },
    ],
  },
  results: {
    title: 'Aide — Resultats',
    tips: [
      { title: 'Publier les classements', text: 'Apres import du chronometrage, les classements sont generes automatiquement.' },
      { title: 'Consultation publique', text: 'Les participants peuvent voir leurs resultats sur la page de l\'evenement.' },
    ],
  },
  volunteers: {
    title: 'Aide — Benevoles',
    tips: [
      { title: 'Ajouter un benevole', text: 'Cliquez sur "+ Ajouter" et renseignez nom, contact et role assigne.' },
      { title: 'Affecter a un evenement', text: 'Chaque benevole peut etre assigne a un evenement specifique.' },
      { title: 'Filtrer', text: 'Filtrez par evenement pour voir les equipes affectees.' },
    ],
  },
  partners: {
    title: 'Aide — Prestations Externes',
    tips: [
      { title: 'Ajouter un prestataire', text: 'Enregistrez les coordonnees de vos prestataires (traiteur, securite, photographe...).' },
      { title: 'Categoriser', text: 'Classez vos prestataires par categorie pour les retrouver facilement.' },
      { title: 'Notes', text: 'Ajoutez des notes internes pour chaque prestataire (tarif, conditions...).' },
    ],
  },
  bookings: {
    title: 'Aide — Reservations Entreprises',
    tips: [
      { title: 'Reservation de groupe', text: 'Creez une reservation pour une entreprise avec nombre d\'equipes et tarif de groupe.' },
      { title: 'Lien de paiement', text: 'Generez un lien de paiement SumUp pour la reservation.' },
    ],
  },
  sponsors: {
    title: 'Aide — Sponsors & Donateurs',
    tips: [
      { title: 'Ajouter un sponsor', text: 'Enregistrez les details du sponsor : nom, type, montant, contreparties.' },
      { title: 'Types', text: 'Distinguez sponsors (contrepartie) et donateurs/mecenes (sans contrepartie).' },
      { title: 'Suivi financier', text: 'Suivez les montants promis et les paiements recus.' },
    ],
  },
  boutique: {
    title: 'Aide — Boutique Produits Derives',
    tips: [
      { title: 'Catalogue prestataire', text: 'Parcourez le catalogue Toptex pour selectionner des articles a personnaliser (t-shirts, casquettes...).' },
      { title: 'Commander', text: 'Envoyez une demande au prestataire avec votre logo. Il vous enverra un devis et un BAT (Bon a Tirer).' },
      { title: 'Fixer vos prix', text: 'Apres paiement, publiez les produits sur votre evenement en fixant votre marge (15%, 20%, 50%...).' },
      { title: 'Commission 5%', text: 'iBookSport preleve 5% du prix de vente TTC. Le reste est votre benefice net.' },
      { title: 'Suivi des ventes', text: 'L\'onglet "Commandes" affiche toutes les ventes avec le detail des marges et commissions.' },
    ],
  },
  analytics: {
    title: 'Aide — Statistiques',
    tips: [
      { title: 'Graphiques', text: 'Visualisez les tendances d\'inscriptions, revenus et remplissage sur differentes periodes.' },
      { title: 'Comparaison', text: 'Comparez les performances entre vos differents evenements.' },
    ],
  },
  rfid: {
    title: 'Aide — Location RFID',
    tips: [
      { title: 'Materiel', text: 'Louez du materiel de chronometrage RFID (puces, portiques, lecteurs).' },
      { title: 'Reservation', text: 'Reservez a l\'avance pour garantir la disponibilite le jour de votre evenement.' },
    ],
  },
  sms: {
    title: 'Aide — Notifications SMS',
    tips: [
      { title: 'Envoyer un SMS', text: 'Redigez un message court et selectionnez les destinataires (tous ou par evenement).' },
      { title: 'Cas d\'usage', text: 'Ideal pour les rappels de derniere minute, changements de parcours, ou envoi de resultats.' },
      { title: 'Credits', text: 'Chaque SMS consomme un credit. Consultez votre solde avant l\'envoi.' },
    ],
  },
};

const OrganizerOnboarding = ({ activeSection = 'hub' }) => {
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [helpMode, setHelpMode] = useState(false);
  const [helpTarget, setHelpTarget] = useState(null);
  const [helpTooltipPos, setHelpTooltipPos] = useState({ top: 0, left: 0 });
  const [showModuleHelp, setShowModuleHelp] = useState(false);

  // Reset states when section changes
  useEffect(() => {
    setHelpMode(false);
    setHelpTarget(null);
    setShowModuleHelp(false);
  }, [activeSection]);

  useEffect(() => {
    const done = localStorage.getItem(ONBOARDING_KEY);
    if (!done && activeSection === 'hub') {
      const timer = setTimeout(() => setShowWelcome(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const startTour = () => {
    setShowWelcome(false);
    setCurrentStep(0);
    setShowTour(true);
    setTimeout(() => highlightStep(0), 100);
  };

  const highlightStep = (stepIdx) => {
    const s = tourSteps[stepIdx];
    if (!s) return;
    const el = document.querySelector(`[data-testid="${s.target}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setTooltipPos({
          top: rect.bottom + 12,
          left: Math.max(16, Math.min(rect.left + rect.width / 2 - 160, window.innerWidth - 340)),
        });
      }, 300);
    }
  };

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      highlightStep(next);
    } else { endTour(); }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      highlightStep(prev);
    }
  };

  const endTour = () => {
    setShowTour(false);
    setCurrentStep(0);
    localStorage.setItem(ONBOARDING_KEY, 'true');
  };

  const dismissWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem(ONBOARDING_KEY, 'true');
  };

  // Hub help mode
  const toggleHelpMode = () => {
    if (helpMode) { setHelpMode(false); setHelpTarget(null); }
    else { setHelpMode(true); setHelpTarget(null); }
  };

  const handleHelpClick = (e) => {
    if (e.target.closest('[data-testid="onboarding-help-btn"]')) return;
    const hubBtn = e.target.closest('[data-testid^="hub-btn-"]') || e.target.closest('[data-testid="create-event-btn"]');
    if (hubBtn) {
      e.preventDefault();
      e.stopPropagation();
      const testId = hubBtn.getAttribute('data-testid');
      const matched = tourSteps.find(s => s.target === testId);
      if (matched) {
        const rect = hubBtn.getBoundingClientRect();
        setHelpTarget(matched);
        setHelpTooltipPos({
          top: rect.bottom + 12,
          left: Math.max(16, Math.min(rect.left + rect.width / 2 - 160, window.innerWidth - 340)),
        });
      }
    } else {
      setHelpMode(false);
      setHelpTarget(null);
    }
  };

  useEffect(() => {
    if (helpMode) {
      document.addEventListener('click', handleHelpClick, true);
      return () => document.removeEventListener('click', handleHelpClick, true);
    }
  }, [helpMode]);

  const step = tourSteps[currentStep];
  const targetEl = showTour && step ? document.querySelector(`[data-testid="${step.target}"]`) : null;
  const targetRect = targetEl ? targetEl.getBoundingClientRect() : null;

  const isHub = activeSection === 'hub';
  const currentModuleHelp = !isHub ? moduleHelp[activeSection] : null;

  return (
    <>
      {/* ==================== LIGHTBULB HELP BUTTON ==================== */}
      <button
        onClick={() => {
          if (showTour) return;
          if (isHub) { toggleHelpMode(); }
          else { setShowModuleHelp(prev => !prev); }
        }}
        className={`fixed bottom-24 right-6 z-[60] w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 group ${
          helpMode || showModuleHelp
            ? 'bg-brand text-white scale-110'
            : 'bg-asphalt text-amber-300 hover:bg-asphalt-light hover:text-amber-200 hover:scale-105'
        }`}
        data-testid="onboarding-help-btn"
        title="Aide"
        style={{
          boxShadow: helpMode || showModuleHelp
            ? '0 0 20px rgba(255,69,0,0.5), 0 4px 12px rgba(0,0,0,0.3)'
            : '0 0 12px rgba(255,193,7,0.3), 0 4px 12px rgba(0,0,0,0.3)',
        }}
      >
        <Lightbulb className={`w-7 h-7 transition-transform duration-300 ${
          helpMode || showModuleHelp ? 'rotate-12' : 'group-hover:rotate-12'
        }`} />
        {/* Animated glow ring */}
        {!helpMode && !showModuleHelp && (
          <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-amber-400" style={{ animationDuration: '3s' }} />
        )}
      </button>

      {/* ==================== HUB: HELP MODE INDICATOR ==================== */}
      <AnimatePresence>
        {isHub && helpMode && !helpTarget && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-[100px] right-6 z-[60] bg-asphalt text-white px-4 py-2.5 text-sm font-heading uppercase tracking-wider shadow-xl border-l-4 border-brand max-w-[260px]"
          >
            Cliquez sur n'importe quel element
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== HUB: HELP TOOLTIP ==================== */}
      <AnimatePresence>
        {isHub && helpTarget && (
          <motion.div
            key={helpTarget.target}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed z-[70] w-80 bg-asphalt text-white p-5 shadow-2xl border border-white/10"
            style={{ top: helpTooltipPos.top, left: helpTooltipPos.left }}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <h4 className="font-heading font-bold uppercase tracking-wider text-sm">{helpTarget.title}</h4>
              </div>
              <button onClick={() => setHelpTarget(null)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">{helpTarget.description}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== MODULE HELP PANEL ==================== */}
      <AnimatePresence>
        {!isHub && showModuleHelp && currentModuleHelp && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="fixed bottom-[100px] right-6 z-[70] w-80 max-h-[60vh] bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
            data-testid="module-help-panel"
          >
            {/* Header */}
            <div className="bg-asphalt text-white px-5 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-400" />
                <h4 className="font-heading font-bold uppercase tracking-wider text-xs">{currentModuleHelp.title}</h4>
              </div>
              <button onClick={() => setShowModuleHelp(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Tips */}
            <div className="overflow-y-auto flex-1 p-4 space-y-3">
              {currentModuleHelp.tips.map((tip, idx) => (
                <div key={idx} className="border-l-2 border-brand pl-3">
                  <h5 className="font-heading font-bold uppercase tracking-wider text-xs text-asphalt mb-0.5">{tip.title}</h5>
                  <p className="text-slate-500 text-xs leading-relaxed">{tip.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== WELCOME MODAL ==================== */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white max-w-md w-full p-8 text-center"
            >
              <div className="w-16 h-16 bg-amber-50 flex items-center justify-center mx-auto mb-5" style={{ boxShadow: '0 0 24px rgba(255,193,7,0.3)' }}>
                <Lightbulb className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="font-heading text-2xl font-bold uppercase mb-3">
                Bienvenue dans votre espace !
              </h2>
              <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                C'est votre premiere visite. Souhaitez-vous faire un tour guide rapide pour decouvrir les fonctionnalites de votre tableau de bord ?
              </p>
              <div className="flex flex-col gap-3">
                <Button className="btn-primary w-full gap-2" onClick={startTour} data-testid="onboarding-start-tour">
                  Oui, montrez-moi <ArrowRight className="w-4 h-4" />
                </Button>
                <button
                  onClick={dismissWelcome}
                  className="text-slate-400 text-sm hover:text-slate-600 transition-colors font-heading uppercase tracking-wider"
                  data-testid="onboarding-skip"
                >
                  Je connais deja, passer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== GUIDED TOUR OVERLAY ==================== */}
      <AnimatePresence>
        {showTour && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70]"
              style={{ background: 'rgba(0,0,0,0.6)' }}
              onClick={endTour}
            />
            {targetRect && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed z-[71] pointer-events-none"
                style={{
                  top: targetRect.top - 4,
                  left: targetRect.left - 4,
                  width: targetRect.width + 8,
                  height: targetRect.height + 8,
                  border: '2px solid #FF4500',
                  boxShadow: '0 0 0 4000px rgba(0,0,0,0.55)',
                  background: 'transparent',
                }}
              />
            )}
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="fixed z-[72] w-80 bg-white p-5 shadow-2xl border border-slate-200"
              style={{ top: tooltipPos.top, left: tooltipPos.left }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-slate-400 font-heading uppercase tracking-wider">
                  Etape {currentStep + 1}/{tourSteps.length}
                </span>
                <button onClick={endTour} className="text-slate-300 hover:text-slate-600 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="w-full h-1 bg-slate-100 mb-4">
                <div className="h-full bg-brand transition-all duration-300" style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }} />
              </div>
              <h4 className="font-heading font-bold uppercase tracking-wider text-sm mb-2 text-asphalt">{step?.title}</h4>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">{step?.description}</p>
              <div className="flex items-center justify-between">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className={`flex items-center gap-1 text-xs font-heading uppercase tracking-wider ${currentStep === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:text-asphalt'}`}
                >
                  <ChevronLeft className="w-3 h-3" /> Precedent
                </button>
                <Button className="btn-primary !py-2 !px-4 text-xs" onClick={nextStep} data-testid="onboarding-next-btn">
                  {currentStep === tourSteps.length - 1 ? 'Terminer' : 'Suivant'}
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default OrganizerOnboarding;
